import type { GrammarTopic } from './types';

export const fluencyTopics: GrammarTopic[] = [
  {
    slug: 'direct-reported-speech',
    title: 'Direct and Reported Speech',
    blocks: [
      {
        kind: 'p',
        text: 'Direct and indirect speech can be a source of confusion for English learners. Let\'s first define the terms, then look at how to talk about what someone said, and how to convert speech from direct to indirect or vice-versa.',
      },
      {
        kind: 'p',
        text: 'You can answer the question “What did he say?” in two ways:',
      },
      {
        kind: 'list',
        items: [
          'by repeating the words spoken (direct speech)',
          'by reporting the words spoken (indirect or reported speech).',
        ],
      },
      { kind: 'h3', text: 'Direct Speech' },
      {
        kind: 'p',
        text: 'Direct speech repeats, or quotes, the exact words spoken. When we use direct speech in writing, we place the words spoken between quotation marks (" ") and there is no change in these words. We may be reporting something that\'s being said NOW (for example a telephone conversation), or telling someone later about a previous conversation.',
      },
      {
        kind: 'examples',
        items: ['She **says**, "What time **will you be** home?"'],
      },
      { kind: 'h3', text: 'Reported Speech' },
      {
        kind: 'p',
        text: 'Reported or indirect speech is usually used to talk about the past, so we normally change the tense of the words spoken. We use reporting verbs like \'say\', \'tell\', \'ask\', and we may use the word \'that\' to introduce the reported words.',
      },
      {
        kind: 'examples',
        items: ['She **asked** what time I **would be** home.'],
      },
      { kind: 'h3', text: 'How to Pass from the Direct Speech to the Reported Speech' },
      { kind: 'h3', text: '1. Verb Tense' },
      {
        kind: 'p',
        text: 'Normally, the tense in reported speech is one tense back in time from the tense in direct speech:',
      },
      {
        kind: 'examples',
        items: ['She said, "I **am** tired." = She **said that she was** tired.'],
      },
      {
        kind: 'table',
        header: ['Phrase in Direct Speech', 'Equivalent in Reported Speech'],
        rows: [
          ['Simple present', 'Simple past'],
          ['"I always **drink** coffee", she said', 'She said that she always **drank** coffee.'],
          ['Present continuous', 'Past continuous'],
          ['"I **am reading** a book", he explained.', 'He explained that he **was reading** a book'],
          ['Simple past', 'Past perfect'],
          ['"Bill **arrived** on Saturday", he said.', 'He said that Bill **had arrived** on Saturday.'],
          ['Present perfect', 'Past perfect'],
          ['"I **have been** to Spain", he told me.', 'He told me that he **had been** to Spain.'],
          ['Past perfect', 'Past perfect'],
          [
            '"I **had just turned out** the light," he explained.',
            'He explained that he **had just turned out** the light.',
          ],
          ['Present perfect continuous', 'Past perfect continuous'],
          [
            'They complained, "We **have been waiting** for hours".',
            'They complained that they **had been waiting** for hours.',
          ],
          ['Past continuous', 'Past perfect continuous'],
          [
            '"We **were living** in Paris", they told me.',
            'They told me that they **had been living** in Paris until 2020.',
          ],
          ['Future', 'Present conditional'],
          [
            '"I **will be** in Geneva on Monday", he said.',
            'He said that he **would be** in Geneva on Monday.',
          ],
          ['Future continuous', 'Conditional continuous'],
          [
            'She said, "**I\'ll be using** the car next Friday".',
            'She said that she **would be using** the car next Friday.',
          ],
        ],
      },
      { kind: 'h3', text: '2. Time expressions' },
      {
        kind: 'p',
        text: 'Sometimes when we change direct speech into reported speech we have to change time expressions too. We don\'t always have to do this, however. It depends on when we hear the direct speech and when we say the reported speech.',
      },
      {
        kind: 'table',
        header: ['Direct speech', 'Reported speech'],
        rows: [
          ['now', 'then / at that time'],
          ['today', 'yesterday / that day / Tuesday / the 27th of June'],
          [
            'yesterday',
            'the day before yesterday / the day before / Wednesday / the 5th of December',
          ],
          ['last night', 'the night before, Thursday night'],
          ['last week', 'the week before / the previous week'],
          ['tomorrow', 'today / the next day / the following day / Friday'],
        ],
      },
    ],
  },
  {
    slug: 'embedded-questions',
    title: 'Embedded Questions & Sentences',
    blocks: [
      {
        kind: 'p',
        text: 'An embedded question is a question that is reported to other people in speech or writing, rather than the exact words of the original question.',
      },
      {
        kind: 'table',
        header: ['Direct Questions', 'Embedded Questions'],
        rows: [
          ['What time **is** it?', '**Can you tell me what time** it **is**?'],
          ['Where **is** she?', '**Could you tell me where** she **is**?'],
          ['**What’s** her name?', '**Do you know what** her name **is**?'],
          ['Who **is** a doctor?', '**I wonder who is** a doctor.'],
          ['What **is** she doing?', '**I wonder what** she **is doing**.'],
          ['**Can** she win?', '**Can you tell me whether or not** she **can** swim?'],
          ['**Are** you tired?', '**They asked me whether** I **was** tired.'],
          ['**Do** the rooms have showers?', '**They asked whether** the rooms **have** showers or not.'],
          ['**Are** you sick?', '**They asked if** I **was** sick.'],
          ['**Do** the rooms have cable TV?', '**They asked if** the rooms **have** cable TV.'],
        ],
      },
    ],
  },
  {
    slug: 'tag-questions',
    title: 'Tag Questions',
    blocks: [
      {
        kind: 'p',
        text: 'A tag question is a small question that is attached, or "tagged", to the end of a sentence. The form auxiliary verb or modal is used in the tag question.',
      },
      {
        kind: 'examples',
        items: [
          'You came by train, **didn’t you**?',
          'It’s very windy today, **isn’t it**?',
          'You can meet me at the station, **can’t you**?',
          'You couldn’t give me a ride, **could you**?',
        ],
      },
      { kind: 'h3', text: 'Sentence Pattern' },
      { kind: 'h3', text: '1. If the sentence is negative, the tag is usually positive.' },
      {
        kind: 'examples',
        items: [
          'You didn’t tell him, **did you**?',
          'He never drinks alcohol, **does he**?',
          'Nobody left a message, **did they**?',
        ],
      },
      { kind: 'h3', text: '2. If the sentence is positive, the tag is usually negative.' },
      {
        kind: 'examples',
        items: [
          'You told him, **didn’t you**?',
          'I’m on time, **aren’t I**? → Ain’t I?',
          'You’ll bring other things, **won’t you**?',
        ],
      },
    ],
  },
  {
    slug: 'gerunds-infinitives',
    title: 'Gerunds & Infinitives: -ing & To',
    blocks: [
      { kind: 'h3', text: 'What Are Gerunds and Infinitives?' },
      {
        kind: 'list',
        items: [
          'Gerunds: Verb + -ing (e.g., working, reading, writing). A gerund functions as a noun in a sentence.',
          'Infinitives: To + base form of the verb (e.g., to work, to read, to write). An infinitive can function as a noun, adjective, or adverb.',
        ],
      },
      { kind: 'h3', text: 'I. Uses of Gerunds' },
      { kind: 'h3', text: 'a. Gerunds as the subject of a sentence' },
      { kind: 'p', text: 'When the action itself is the focus of the sentence.' },
      { kind: 'examples', items: ['**Swimming** is a great way to stay fit.'] },
      { kind: 'h3', text: 'b. Gerunds after prepositions (and phrasal verbs)' },
      { kind: 'p', text: 'Gerunds are always used after prepositions.' },
      {
        kind: 'examples',
        items: [
          'She is interested **in learning** new skills.',
          'They **gave up trying** to fix the car.',
        ],
      },
      { kind: 'h3', text: 'c. Gerunds after possessives' },
      {
        kind: 'p',
        text: 'When emphasizing the "doer" of the action, use a possessive before the gerund.',
      },
      { kind: 'examples', items: ['I appreciate **your helping** me.'] },
      { kind: 'h3', text: 'd. Gerunds after certain verbs' },
      {
        kind: 'p',
        text: 'Some verbs are always followed by gerunds. Common examples include: enjoy, avoid, consider, suggest, finish, practice, mind, keep.',
      },
      {
        kind: 'examples',
        items: [
          'He **enjoys playing** tennis.',
          'I don’t **mind spending** every day out on your corner in the pouring rain.',
          'I would **avoid smoking** electronic cigarettes.',
        ],
      },
      { kind: 'h3', text: 'II. Uses of Infinitives' },
      { kind: 'h3', text: 'a. Infinitives to express purpose (in order to)' },
      { kind: 'p', text: 'Infinitives are often used to explain the reason for an action.' },
      {
        kind: 'examples',
        items: ['She left early **to catch** her train.', 'Her goal is **to win** the competition.'],
      },
      { kind: 'h3', text: 'b. Infinitives after adjectives' },
      { kind: 'p', text: 'When expressing emotions, opinions, or characteristics.' },
      {
        kind: 'examples',
        items: [
          'It’s **important to study** regularly.',
          'I find it **hard to save** money.',
          'I find it **interesting to travel** abroad.',
        ],
      },
      { kind: 'h3', text: 'c. Infinitives after certain verbs' },
      {
        kind: 'p',
        text: 'Certain verbs are always followed by infinitives. Common examples include: want, need, go, try, decide, hope, plan, promise, learn, expect, fail.',
      },
      { kind: 'examples', items: ['They **decided to leave** early.'] },
      { kind: 'h3', text: 'd. Infinitives with "too" and "enough".' },
      {
        kind: 'examples',
        items: ['He’s **too tired to continue**.', 'She’s **strong enough to lift** that box.'],
      },
      { kind: 'h3', text: 'Rule of Thumb!' },
      { kind: 'h3', text: 'a. Use a gerund after a preposition (e.g., of, about, in, at).' },
      { kind: 'examples', items: ['She is tired **of waiting**.'] },
      { kind: 'h3', text: 'b. Use an infinitive to express purpose.' },
      { kind: 'examples', items: ['She studies **to improve** her skills.'] },
    ],
  },
  {
    slug: 'gerunds-infinitives-special-cases',
    title: 'Special Cases with Gerunds & Infinitives',
    blocks: [
      {
        kind: 'p',
        text: 'Some verbs can be followed by both the gerund and the infinitive form. However, the meaning conveyed changes.',
      },
      { kind: 'h3', text: '1. STOP' },
      {
        kind: 'p',
        text: 'When followed by an infinitive, it means that the person stopped what she was doing in order to do something else. When followed by a gerund it means that the person quit a habit.',
      },
      {
        kind: 'examples',
        items: [
          'She **stopped to get** her hair cut at that place.',
          'She **stopped getting** her hair cut at that place.',
        ],
      },
      { kind: 'h3', text: '2. REMEMBER' },
      {
        kind: 'p',
        text: 'When followed by the infinitive, it means that something forgotten came to one’s mind and then one did that. When followed by the gerund, it means that one is having a memory about something completed in the past.',
      },
      {
        kind: 'examples',
        items: ['She **remembered to call** her father.', 'She **remembered calling** her father.'],
      },
      { kind: 'h3', text: '3. FORGET' },
      {
        kind: 'p',
        text: 'The idea of forget follows the same pattern as with “remember”, but with the meaning of forget. When followed by a gerund, one is mentioning a memory.',
      },
      {
        kind: 'examples',
        items: [
          'I’ll never **forget to pick** you up from the airport.',
          'I’ll never **forget picking** you up from the airport.',
        ],
      },
      { kind: 'h3', text: '4. TRY' },
      {
        kind: 'p',
        text: 'When followed by the infinitive, it implies failure, a goal that was not reached. When followed by a gerund, it refers to a method that was used to achieve a certain goal, implying that the goal was actually achieved.',
      },
      {
        kind: 'examples',
        items: [
          'They **tried to make** me go to a rehab and I said no, no, no.',
          'They **tried making** me go to a rehab to sober up.',
        ],
      },
      { kind: 'h3', text: '5. REGRET' },
      {
        kind: 'p',
        text: 'When followed by the infinitive, it refers to a present regret, something that is just now coming up. When followed by the gerund, it refers to an action that was already taken in the past and that now is regretted.',
      },
      {
        kind: 'examples',
        items: [
          'The government **regrets to announce** they are closing schools.',
          'The government **regrets announcing** they are closing schools.',
        ],
      },
      { kind: 'h3', text: '6. GO ON' },
      {
        kind: 'p',
        text: 'When followed by the infinitive, it means that a previous activity continued the same. When followed by the gerund, it means that an activity was interrupted and now a new one is coming.',
      },
      {
        kind: 'examples',
        items: [
          'Mellina **went on** (continued) **talking** about gerunds and infinitives.',
          'Mellina **went on** (moved on) **to talk** about gerunds and infinitives.',
        ],
      },
    ],
  },
  {
    slug: 'to-vs-for',
    title: 'To vs. For: How to Use These Prepositions',
    blocks: [
      {
        kind: 'p',
        text: 'To and for are similar, so it’s easy to get them confused. Both to and for are prepositions and both are used quite a lot. The difference between to and for lies in their meanings and the words they’re typically used with.',
      },
      { kind: 'h3', text: 'Using To' },
      { kind: 'h3', text: '1. Direction' },
      { kind: 'p', text: 'Expressing movement toward something' },
      {
        kind: 'examples',
        items: [
          'We’re going **to the zoo** this weekend.',
          'Look **to the left** and right when crossing the street.',
        ],
      },
      { kind: 'h3', text: '2. Receiving' },
      { kind: 'p', text: 'Showing who or what receives an action or item' },
      {
        kind: 'examples',
        items: [
          'Furlong passed the ball **to Sexton** at the last second.',
          'We didn’t invite Karen because she was always mean **to us**.',
        ],
      },
      { kind: 'h3', text: '3. Connection' },
      { kind: 'p', text: 'Showing an abstract link between two people' },
      {
        kind: 'examples',
        items: [
          'I am married **to my best friend**.',
          'This is Dwight, assistant **to the regional manager**.',
        ],
      },
      { kind: 'h3', text: '4. Attachment' },
      { kind: 'p', text: 'Showing a physical attachment between two things' },
      {
        kind: 'examples',
        items: [
          'The piece of paper was accidentally glued **to my hand**.',
          'I chained my bike **to a bench**, but the bench was stolen.',
        ],
      },
      { kind: 'h3', text: '5. Comparison' },
      { kind: 'p', text: 'Introducing the second part of a comparison' },
      {
        kind: 'examples',
        items: [
          'High school is easy compared **to college**.',
          'They preferred water **to soda**.',
        ],
      },
      { kind: 'h3', text: '6. Reason (with verb)' },
      {
        kind: 'p',
        text: 'Describing why something happened, when the explanation uses a verb.',
      },
      {
        kind: 'examples',
        items: [
          'He shouldn’t have joined the army **to impress** his parents.',
          'I study so much **to get** good grades.',
        ],
      },
      { kind: 'h3', text: 'Other main uses of to' },
      { kind: 'h3', text: 'a. Infinitives' },
      { kind: 'p', text: 'In the infinitive form of a verb, to comes before the root verb.' },
      {
        kind: 'examples',
        items: ['I want **to be** an astronaut.', '**To succeed** in anything requires motivation.'],
      },
      { kind: 'h3', text: 'b. Time' },
      {
        kind: 'p',
        text: 'Especially in British English, to is used to mean “before” a certain time.',
      },
      {
        kind: 'examples',
        items: ['It’s a quarter **to four**. (3:45)', 'Ten minutes **to closing**!'],
      },
      { kind: 'h3', text: 'c. Synonym of “until”' },
      { kind: 'p', text: 'To is often used colloquially as a replacement for “until.”' },
      {
        kind: 'examples',
        items: [
          'We’re open from dawn **to dusk**.',
          'I’m with you **to the end** of the line, Bucky.',
        ],
      },
      { kind: 'h3', text: 'When to Use For' },
      { kind: 'h3', text: '1. Indicating agreement or favor toward something.' },
      {
        kind: 'examples',
        items: [
          'I always root **for the underdog** at games.',
          'If you’re not **for the union**, you’re against it!',
        ],
      },
      { kind: 'h3', text: '2. Representation' },
      {
        kind: 'p',
        text: 'Showing an action on behalf of or representing something else.',
      },
      {
        kind: 'examples',
        items: [
          'I speak **for everyone** when I say that we’re happy you’re here.',
          'I may work **for Walmart**, but I don’t shop there.',
        ],
      },
      { kind: 'h3', text: '3. Exchange' },
      { kind: 'p', text: 'Introducing the second part of a purchase, deal, or trade.' },
      {
        kind: 'examples',
        items: [
          'I’ll give you my potato chips **for your mini-muffin**.',
          'He bought the entire Skrillex discography **for only a dollar**.',
        ],
      },
      { kind: 'h3', text: '4. Length' },
      { kind: 'p', text: 'Indicating length of time or distance.' },
      {
        kind: 'examples',
        items: [
          'She worked there **for four years** and the boss never learned her name.',
          'Go straight **for a few miles** and then turn left.',
        ],
      },
      { kind: 'h3', text: '5. Gratitude' },
      { kind: 'p', text: 'Showing thanks or gratitude.' },
      {
        kind: 'examples',
        items: [
          'Thank you **for your email**.',
          'It isn’t much, but I’m grateful **for what I have**.',
        ],
      },
      { kind: 'h3', text: '6. Reason (with noun)' },
      {
        kind: 'p',
        text: 'Describing why something happened, when the explanation uses a noun.',
      },
      {
        kind: 'examples',
        items: [
          'She became a doctor **for the money**, not the long hours.',
          'We have to practice extra **for the tournament**.',
        ],
      },
      { kind: 'h3', text: 'Other main uses of for' },
      { kind: 'h3', text: 'a. Conjunction' },
      {
        kind: 'p',
        text: 'For is a coordinating conjunction that acts as a synonym of “because,” although today it’s a little outdated.',
      },
      {
        kind: 'examples',
        items: [
          'Don’t go out past midnight, **for** it’s not safe.',
          'The crops aren’t growing, **for** the weather is lousy.',
        ],
      },
      { kind: 'h3', text: 'b. Comparisons with what’s normal' },
      {
        kind: 'p',
        text: 'You can use for to emphasize how something is different from the norm or general standard.',
      },
      {
        kind: 'examples',
        items: [
          'She’s tall **for her age**.',
          '“You’re not bad **for an elf**,” said the dwarf.',
        ],
      },
      { kind: 'h3', text: 'c. Scheduling' },
      { kind: 'p', text: 'For is used to indicate a planned arrangement in the future.' },
      {
        kind: 'examples',
        items: [
          'I have a dentist appointment scheduled **for Tuesday**.',
          'Is our D&D session still on **for later**?',
        ],
      },
    ],
  },
  {
    slug: 'academic-writing-tenses',
    title: 'Verb Tenses in Academic Writing',
    blocks: [
      {
        kind: 'p',
        text: 'While there are several different types of verb tenses, the foundational tenses are past, present, and future. These are called simple verb tenses. Besides that, there are also the perfect verb tenses and the progressive or continuous verb tenses. Let’s take a look at each of these simple verb tenses and how they are used in academic writing.',
      },
      { kind: 'h3', text: '1. Present Simple' },
      {
        kind: 'p',
        text: 'The present simple is used to talk about habits, routines, general truths, and facts. Its auxiliary verb is DO and DOES.',
      },
      {
        kind: 'examples',
        items: [
          'I **wake up** every day at 7 am but my brother **doesn’t**. He **wakes up** at 9 am. → Routine, habit',
          'At what temperature **does** water **boil**? Water **boils** at 100 degrees. → fact, general truth',
        ],
      },
      { kind: 'h3', text: 'How is Simple Present Tense Used in Academic Writing?' },
      {
        kind: 'p',
        text: 'The present tense is dominant in most forms of academic work, since it applies when writing about current events or states of being. This includes describing:',
      },
      { kind: 'p', text: 'Existing facts and theories:' },
      { kind: 'examples', items: ['Profane language **is** common among young people…'] },
      { kind: 'p', text: 'The findings of a study:' },
      { kind: 'examples', items: ['The results **demonstrate** that…'] },
      { kind: 'p', text: 'The opinions or claims of other thinkers:' },
      { kind: 'examples', items: ['Cook and Moore **argue** that…'] },
      {
        kind: 'p',
        text: 'The present tense is usually correct even when describing a study that happened in the past, as long as the conclusions are still relevant in the present.',
      },
      { kind: 'h3', text: '2. Past Simple' },
      {
        kind: 'p',
        text: 'The simple past is used to talk about past facts, tell stories and past anecdotes, and report things that happened in the past and do not influence the present moment. Its auxiliary verb is DID.',
      },
      {
        kind: 'examples',
        items: [
          'Last month, my family and I **travelled** together and it **was** one of the happiest moments of my life. We **didn’t know** how much we **needed** these moments together.',
        ],
      },
      { kind: 'h3', text: 'How is Simple Past Tense Used in Academic Writing?' },
      {
        kind: 'p',
        text: 'In an academic paper, you could use the past tense to show that an idea is not widely accepted any more. In the following, for instance, the past tense “claimed” and “has since been disputed” both signal that the study no longer applies:',
      },
      {
        kind: 'examples',
        items: [
          'Cook and Moore (1964) originally **claimed** that profane language is amusing, although this **has since been disputed** by many experts.',
        ],
      },
      {
        kind: 'p',
        text: 'The past tense is also commonly used in academic writing when describing the methods used in an experiment that has already been conducted. The method section is usually written in the passive voice.',
      },
      { kind: 'examples', items: ['The sample **was tested** using several techniques.'] },
      {
        kind: 'p',
        text: 'Some colleges have specific instructions for how a methodology chapter should be written, though, so make sure to check for rules about tense use in your style guide.',
      },
      { kind: 'h3', text: '3. Future Simple' },
      {
        kind: 'p',
        text: 'The simple future is used to talk about future events, predictions, and probabilities. Its auxiliary verb is WILL.',
      },
      {
        kind: 'examples',
        items: [
          'I **will try** to finish my English homework tomorrow but if I can’t, I **won’t worry**.',
        ],
      },
      { kind: 'h3', text: 'How is Simple Future Tense Used in Academic Writing?' },
      {
        kind: 'p',
        text: 'The future tense is less common in academic writing, but it still has a couple of important roles. One is in research proposals, since you’ll need to describe your research aims, predictions about results, and the methods you intend to use:',
      },
      {
        kind: 'examples',
        items: [
          'This study **will examine** the role of profane language in comedy. We predict that profane language **will be considered** hilarious by the majority of respondents.',
        ],
      },
      {
        kind: 'p',
        text: 'The future tense is also useful when recommending fresh avenues of research or suggesting how the results of a study could be applied:',
      },
      {
        kind: 'examples',
        items: [
          'Our study suggests that further research **should be conducted** into the increasing use of profane language in everyday life.',
        ],
      },
      {
        kind: 'p',
        text: 'The crucial thing is that the future tense is used when describing something that hasn’t yet happened or that is expected to occur in the future.',
      },
    ],
  },
  {
    slug: 'substitution-avoiding-repetition',
    title: 'Substitution & Avoiding Repetition',
    blocks: [
      {
        kind: 'p',
        text: 'In speaking and in writing, we try to avoid repeating words, phrases or clauses so we use substitute forms to do this. Knowledge of these forms will improve the quality of your fluency and turn you into a more proficient speaker.',
      },
      {
        kind: 'p',
        text: 'We can use many different words and phrases in substitution, including words such as ‘both’, ‘either’, ‘some’ (indefinite quantifying pronouns), ‘do’ and ‘so’.',
      },
      { kind: 'h3', text: '1. Subject, Object and Possessive Pronouns' },
      {
        kind: 'p',
        text: 'Used in place of noun phrases, usually to refer back to people and things already mentioned.',
      },
      {
        kind: 'examples',
        items: [
          'I can speak Japanese quite well. I’ve been learning **it** for several years.',
          'Your English is SO great, **mine** is a little bit rusty. → my English',
          'To ensure that our sales staff speak to customers in an appropriate way, management has made the decision to send **them** on a training course next week',
        ],
      },
      { kind: 'h3', text: '2. Indefinite pronouns' },
      {
        kind: 'p',
        text: 'These include words like “little”, “many”, “enough”, “none”.',
      },
      {
        kind: 'examples',
        items: [
          'There were two choices on the menu but I didn’t like **either**.',
          'Which flavor do you want: chocolate or vanilla? I’ll have **both**.',
          'The company spent a lot of money for the staff trip but only **a few** signed up to go.',
        ],
      },
      { kind: 'h3', text: '3. Do, do so, do it, do the same' },
      {
        kind: 'p',
        text: 'Are used to substitute for a verb and whatever accompanies it (complement).',
      },
      {
        kind: 'examples',
        items: [
          'None of the team understood the problem at first, but they **did so** after a few days.',
          'I always have a shower before breakfast and my wife **does so** too.',
          '‘Move those books off that chair.’ ‘I have already **done so**.’',
        ],
      },
      { kind: 'h3', text: '4. So and such' },
      {
        kind: 'p',
        text: 'So is often used as an adjective, an object clause, and with reporting verbs. Such is used to mean ‘this or that kind’.',
      },
      {
        kind: 'examples',
        items: [
          'The service has always been bad and it is **so** today. (adjective)',
          'Many people believe that we need to recycle more, and I think **so** too. (object clause)',
          'The local authority spent over $5 million on a new logo. Everyone agreed that **such** extravagance could not be justified.',
        ],
      },
      { kind: 'h3', text: '5. One, some, ones' },
      { kind: 'p', text: 'These are used to substitute countable nouns.' },
      {
        kind: 'examples',
        items: [
          'They looked at several houses but couldn’t find **one** they both liked.',
          'The spiders you want to avoid are the small red **ones**.',
        ],
      },
      { kind: 'h3', text: '5. Verb-noun transformations' },
      {
        kind: 'p',
        text: 'We can avoid repetition of a verb or noun in a following sentence by replacing the verb with its noun equivalent and vice-versa.',
      },
      {
        kind: 'examples',
        items: [
          'Many people today **assume** that famine could no longer happen in the developed world. However, I am not so sure that this is a valid **assumption**.',
        ],
      },
    ],
  },
  {
    slug: 'one-generic-pronouns',
    title: 'One & One’s: Generic Pronouns',
    blocks: [
      {
        kind: 'h3',
        text: '1. As a personal pronoun (both subject and object), one can be used to refer to ‘people in general’.',
      },
      {
        kind: 'p',
        text: 'We often use one in making generalizations, especially in more formal styles. One takes a third person singular verb:',
      },
      {
        kind: 'examples',
        items: [
          '**One** never knows, does **one**?',
          '**One** should not use mobile phones when driving.',
          'Holidays are supposed to allow **one** to forget about work.',
        ],
      },
      {
        kind: 'note',
        text: 'If one is used too much, it can make the speaker sound too formal.',
      },
      {
        kind: 'h3',
        text: '2. As a possessive determiner, one’s (add ‘s to it, due to the genitive case) means “your” and is always followed by a noun.',
      },
      {
        kind: 'examples',
        items: [
          '**One’s** health is much more important than having lots of money.',
          '**One** should mind **one’s** business.',
        ],
      },
      {
        kind: 'h3',
        text: '3. As an impersonal pronoun, one can be used with these structures, referring to something previously mentioned:',
      },
      { kind: 'h3', text: 'a. one of + superlative adjectives + plural nouns' },
      { kind: 'examples', items: ['**One of the most modern cities** in Iran is Tabriz.'] },
      { kind: 'h3', text: 'b. one of + possessive determiners + plural nouns' },
      { kind: 'examples', items: ['**One of my books** is there.'] },
      { kind: 'h3', text: 'c. one of + object pronouns' },
      {
        kind: 'examples',
        items: [
          'My brother has so many students, but he likes **one of them** more than the others.',
        ],
      },
      { kind: 'h3', text: 'd. the one(s) + that/which/who' },
      {
        kind: 'examples',
        items: ['I have so many books. **The one that** I like the most is over there.'],
      },
      { kind: 'h3', text: 'e. this one/that one/these ones/those ones' },
      {
        kind: 'examples',
        items: ['This book is awesome. **This one** is a rubbish and **those ones** are the best.'],
      },
      { kind: 'h3', text: 'Mind your consistency!' },
      {
        kind: 'p',
        text: 'If you decide to use “one” as your generic pronoun, you should keep using it to have a consistent speech and writing.',
      },
      {
        kind: 'examples',
        items: [
          'RIGHT: **One** never knows what **one’s** future will bring.',
          'NOT: **You** never know what **one’s** future will bring.',
        ],
      },
      {
        kind: 'h3',
        text: '4. Another commonly used generic pronoun is “they”, used as a singular “he” or “she”.',
      },
      {
        kind: 'p',
        text: 'We use they, them or their to refer to one generic person who could be male or female.',
      },
      {
        kind: 'examples',
        items: [
          'A good journalist should never reveal where **they** get **their** information.',
          'Before the class, every student said **their** name aloud.',
        ],
      },
    ],
  },
  {
    slug: 'causative-sentences',
    title: 'Causative Sentences',
    blocks: [
      {
        kind: 'p',
        text: 'Causatives are used to show how someone or something initiates or influences an action, clarifying the connection between the initiator and the activity. They help express who is responsible for making things happen in different contexts.',
      },
      {
        kind: 'examples',
        items: [
          'She **made him apologize**.',
          'I **got my hair cut** (by my sister).',
          'Peter **let Sara do** the project.',
        ],
      },
      { kind: 'h3', text: 'Active Causative' },
      {
        kind: 'p',
        text: 'In active causative sentences, the subject causes someone else to perform an action. The focus is on who is making the action happen, not necessarily who is performing it.',
      },
      {
        kind: 'examples',
        items: [
          'She **let me drive** her car.',
          'My mom **made me clean** my room.',
          'He **got her to read** the book',
          'He **forced me to study** math at school.',
        ],
      },
      { kind: 'h3', text: 'Passive Causative' },
      {
        kind: 'p',
        text: 'In passive causative sentences, the subject causes something to be done by someone, but the emphasis is on the action itself rather than who performs it. This structure is often used when the specific agent (the person doing the action) is either unknown, unimportant, or understood from context.',
      },
      {
        kind: 'examples',
        items: [
          'She **got her car fixed**.',
          'I **got my nails done**.',
          'I **had my hair cut**.',
          'He **will have his car repaired**.',
        ],
      },
      {
        kind: 'p',
        text: 'If you want to practice: https://www.perfect-english-grammar.com/causatives-exercise-1.html',
      },
    ],
  },
  {
    slug: 'english-collocations',
    title: 'English Collocations',
    blocks: [
      { kind: 'p', text: 'Collocation is a predictable combination of words.' },
      {
        kind: 'p',
        text: 'For example, we can say heavy rain but not strong rain because it does not sound right. Likewise, we can say ‘do exercise’ but not ‘make exercise’.',
      },
      {
        kind: 'p',
        text: 'Collocations can be made up of any kinds of words such as verbs, nouns, adverbs and adjectives. There are no rules for collocations, they are just combinations of words that we can become familiar with and then use correctly. To better speak English, we should understand and know some collocations and simple mistakes.',
      },
      {
        kind: 'examples',
        items: [
          'You **made a mistake**. → not did a mistake.',
          'I have to **make a big decision**. → not a large decision',
          'She **committed a crime**. → not perform a crime.',
          'Yesterday there was **heavy rain**. → not strong rain',
          'The city has been having **high temperatures** this summer. → not tall temperature',
          'I **had a wonderful experience** with my family. → not did an experience',
          'She has **blonde hair**. → not yellow hair',
          'I need to **make the bed** every day. → not do the bed',
          'Some people don’t **take risks** in life. → not get risks',
        ],
      },
      { kind: 'h3', text: 'Verb Collocations' },
      {
        kind: 'p',
        text: 'Some of the most common collocations involve verb + noun collocations used in everyday situations. Here are some examples of the types of verb collocations you will need as you continue learning English.',
      },
      {
        kind: 'table',
        header: ['Collocation', 'Example'],
        rows: [
          ['to feel free', 'Please **feel free** to take a seat and enjoy the show.'],
          ['to come prepared', 'Make sure to **come prepared** for the test tomorrow.'],
          [
            'to save time',
            'You\'ll **save time** if you turn off your smartphone and concentrate on the lesson.',
          ],
          [
            'to find a replacement',
            'We need to **find a replacement** for Jim as soon as possible.',
          ],
          ['to make progress', 'We\'re **making progress** on the project at work.'],
          [
            'to do the washing up',
            'I\'ll **do the washing up** and you can put Johnny to bed.',
          ],
        ],
      },
      { kind: 'h3', text: 'Business English Collocations' },
      {
        kind: 'p',
        text: 'Collocations are often used in business and work settings. There are a number of forms including adjectives, nouns and other verbs that combine with keywords to form business expressions. Here are some business collocations for specific situations.',
      },
      {
        kind: 'table',
        header: ['Collocation', 'Example'],
        rows: [
          ['to open an account', 'Would you like to **open an account** at our bank?'],
          ['to forgive a debt', 'Do you think the bank would **forgive a debt**?'],
          ['to land a deal', 'We **landed a deal** worth $3 million.'],
          [
            'to key in a PIN',
            'Just **key in your PIN** at the ATM and you can make a deposit.',
          ],
          ['to deposit a check', 'I\'d like to **deposit this check** for $100.'],
          [
            'hard-earned money',
            'Once you get a job, you\'ll know what **hard-earned money** really is.',
          ],
          ['to close a deal', 'I **closed a deal** on a new account last week.'],
          ['to write up a contract', 'Let\'s **write up your contract**.'],
          [
            'counterfeit money',
            'Be on the lookout for **counterfeit money** in circulation.',
          ],
        ],
      },
      { kind: 'p', text: 'Sources:' },
      {
        kind: 'list',
        items: [
          'https://www.thoughtco.com/collocation-examples-1210325',
          'https://dictionary.cambridge.org/grammar/british-grammar/collocation',
        ],
      },
      {
        kind: 'p',
        text: 'While collocations are important to our fluency, they need to be used naturally and not memorized. To learn them, you should be aware of the language and you should be able to notice them.',
      },
    ],
  },
  {
    slug: 'debating-useful-language',
    title: 'Debating: Useful Language',
    blocks: [
      {
        kind: 'p',
        text: 'Knowing how to debate in an additional language is not an easy task. To help you articulate your arguments and engage in a structured debate, here are some useful phrases categorized by different functions you may need during a debate:',
      },
      { kind: 'h3', text: '1. Stating Your Position' },
      {
        kind: 'list',
        items: [
          '"**I strongly believe that**..."',
          '"**From my perspective**..."',
          '"**It seems clear to me that**..."',
          '"**In my opinion, the most important aspect is**..."',
        ],
      },
      { kind: 'h3', text: '2. Presenting Your Main Point' },
      {
        kind: 'list',
        items: [
          '"**The key point I want to make is**..."',
          '"**My primary argument is that**..."',
          '"**First and foremost, we need to consider**..."',
          '"**To begin with, let’s examine**..."',
        ],
      },
      { kind: 'h3', text: '3. Providing Reasons' },
      {
        kind: 'list',
        items: [
          '"**One reason why I think this is**..."',
          '"**This is supported by the fact that**..."',
          '"**A crucial point to consider is**..."',
          '"**There are several reasons for this, including**..."',
        ],
      },
      { kind: 'h3', text: '4. Giving Examples' },
      {
        kind: 'list',
        items: [
          '"**For example**,..."',
          '"**Take the case of**..."',
          '"**Consider the situation when**..."',
          '"**To illustrate this point, let’s look at**..."',
        ],
      },
      { kind: 'h3', text: '5. Using Evidence' },
      {
        kind: 'list',
        items: [
          '"**According to recent studies**..."',
          '"**Research shows that**..."',
          '"**Statistics indicate that**..."',
          '"**Experts argue that**..."',
        ],
      },
      { kind: 'h3', text: '6. Full Agreement' },
      {
        kind: 'list',
        items: [
          '"**I completely agree with that point**."',
          '"**I see your point and agree entirely**."',
          '"**You’re absolutely right about that**."',
          '"**That’s exactly how I see it too**."',
        ],
      },
      { kind: 'h3', text: '7. Partial Agreement' },
      {
        kind: 'list',
        items: [
          '"**I agree with you up to a point, but**..."',
          '"**I see where you’re coming from, but**..."',
          '"**While I agree with some aspects, I think**..."',
          '"**That’s a valid point, however**..."',
        ],
      },
      { kind: 'h3', text: '8. Polite Disagreement' },
      {
        kind: 'list',
        items: [
          '"**I see your point, but I respectfully disagree**."',
          '"**While that might be true, I believe that**..."',
          '"**I understand your perspective, but**..."',
          '"**That’s an interesting view, but I think**..."',
        ],
      },
      { kind: 'h3', text: '9. Strong Disagreement' },
      {
        kind: 'list',
        items: [
          '"**I strongly disagree with that argument**."',
          '"**I’m afraid I don’t share your view on this**."',
          '"**There’s no evidence to support that claim**."',
          '"**On the contrary, I believe that**..."',
        ],
      },
      { kind: 'h3', text: '10. Seeking Clarification' },
      {
        kind: 'list',
        items: [
          '"**Can you explain what you mean by**...?"',
          '"**Could you clarify that point, please**?"',
          '"**I’m not sure I understand. Could you elaborate**?"',
          '"**What do you mean when you say**...?"',
        ],
      },
      { kind: 'h3', text: '11. Clarifying Your Own Point' },
      {
        kind: 'list',
        items: [
          '"**Let me put that another way**..."',
          '"**What I meant to say was**..."',
          '"**To clarify, my point is that**..."',
          '"**In other words, I’m suggesting that**..."',
        ],
      },
      { kind: 'h3', text: '12. Counter-Arguments' },
      {
        kind: 'list',
        items: [
          '"**However, we should also consider**..."',
          '"**That’s a good point, but there’s another side to it**."',
          '"**While that’s a valid argument, it’s important to note that**..."',
          '"**On the other hand, you could argue that**..."',
        ],
      },
      { kind: 'h3', text: '13. Rebutting' },
      {
        kind: 'list',
        items: [
          '"**That argument doesn’t take into account that**..."',
          '"**I disagree because**..."',
          '"**The problem with that viewpoint is that**..."',
          '"**Your argument fails to consider that**..."',
        ],
      },
      { kind: 'h3', text: '14. Summarizing' },
      {
        kind: 'list',
        items: [
          '"**In conclusion, the main point is**..."',
          '"**To sum up, we need to**..."',
          '"**Overall, my position is that**..."',
          '"**Ultimately, this leads us to the conclusion that**..."',
        ],
      },
    ],
  },
];
